import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Clock, X } from 'lucide-react';
import { useCalendarEvents, useCreateEvent, useUpdateEvent, useDeleteEvent } from '../../hooks/useCalendar';
import { StudentEvent, EVENT_TYPE_COLORS, CreateEventInput, UpdateEventInput } from '../../types/calendar';
import { SubjectWithThreads } from '../../types/subjects';
import { AddEventModal } from './AddEventModal';

interface StudentCalendarProps {
  subjects: SubjectWithThreads[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

function isPastDate(date: Date, today: Date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d < t;
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T09:00`;
}

export const StudentCalendar: React.FC<StudentCalendarProps> = ({ subjects }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<StudentEvent | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [modalDefaultDate, setModalDefaultDate] = useState<string>('');
  const popoverRef = useRef<HTMLDivElement>(null);

  const start = new Date(viewYear, viewMonth, 1).toISOString();
  const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59).toISOString();

  const { data: events = [], isLoading } = useCalendarEvents(start, end);
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const goToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };

  const eventsByDate = useMemo(() => {
    const map = new Map<string, StudentEvent[]>();
    events.forEach(ev => {
      const d = new Date(ev.event_date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    });
    return map;
  }, [events]);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter(e => new Date(e.event_date) >= now)
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
      .slice(0, 7);
  }, [events]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDay) return [];
    const key = `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`;
    return eventsByDate.get(key) || [];
  }, [selectedDay, eventsByDate]);

  const handleCreate = (input: CreateEventInput) => {
    createEvent.mutate(input);
  };
  const handleUpdate = (id: string, input: UpdateEventInput) => {
    updateEvent.mutate({ id, input });
  };
  const handleDelete = (id: string) => {
    deleteEvent.mutate(id);
    setEditingEvent(null);
  };

  const openNewEvent = (defaultDate?: Date) => {
    setEditingEvent(null);
    setModalDefaultDate(defaultDate ? formatDateForInput(defaultDate) : '');
    setIsModalOpen(true);
    closePopover();
  };

  const openEditEvent = (ev: StudentEvent) => {
    setEditingEvent(ev);
    setModalDefaultDate('');
    setIsModalOpen(true);
    closePopover();
  };

  const closePopover = useCallback(() => {
    setSelectedDay(null);
    setPopoverPos(null);
  }, []);

  const handleDayClick = (date: Date, e: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedDay && isSameDay(date, selectedDay)) {
      closePopover();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    let top = rect.bottom + 4;
    let left = rect.left;
    if (left + 260 > viewportW) left = viewportW - 270;
    if (left < 10) left = 10;
    if (top + 200 > viewportH) top = rect.top - 204;
    setSelectedDay(date);
    setPopoverPos({ top, left });
  };

  useEffect(() => {
    if (!popoverPos) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        closePopover();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [popoverPos, closePopover]);

  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">My Calendar</h2>
            <p className="text-sm text-[var(--text-secondary)]">
              Track exams, assignments, and important dates
            </p>
          </div>
        </div>
        <button
          onClick={() => openNewEvent()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--surface-elevated)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm text-[var(--text-primary)]"
        >
          <Plus className="w-4 h-4 text-[var(--accent-primary)]" />
          Add Event
        </button>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h3 className="text-base font-semibold text-[var(--text-primary)] min-w-[160px] text-center">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </h3>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button onClick={goToday} className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] border border-[var(--border-color)] transition-colors">
          Today
        </button>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] overflow-hidden mb-6">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-[var(--border-color)]">
          {WEEKDAYS.map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`pad-${i}`} className="h-20 border-b border-r border-[var(--border-color)] bg-[var(--bg-primary)]/30" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const date = new Date(viewYear, viewMonth, day);
            const key = `${viewYear}-${viewMonth}-${day}`;
            const dayEvents = eventsByDate.get(key) || [];
            const isToday = isSameDay(date, today);
            const isPast = isPastDate(date, today);
            const isSelected = selectedDay && isSameDay(date, selectedDay);

            return (
              <button
                key={day}
                onClick={(e) => handleDayClick(date, e)}
                className={`h-20 border-b border-r border-[var(--border-color)] p-1.5 text-left flex flex-col transition-colors ${
                  isPast ? 'opacity-40 cursor-default' :
                  isSelected
                    ? 'bg-[var(--accent-primary)]/5 ring-1 ring-inset ring-[var(--accent-primary)]/30'
                    : 'hover:bg-[var(--bg-tertiary)] cursor-pointer'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                  isToday
                    ? 'bg-[var(--accent-primary)] text-white'
                    : isPast
                      ? 'text-[var(--text-tertiary)]'
                      : 'text-[var(--text-primary)]'
                }`}>
                  {day}
                </span>
                {dayEvents.length > 0 && (
                  <div className="flex flex-col gap-0.5 mt-auto min-w-0 overflow-hidden">
                    {dayEvents.slice(0, 2).map((ev) => (
                      <div key={ev.id} className="flex items-center gap-1 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: EVENT_TYPE_COLORS[ev.event_type as keyof typeof EVENT_TYPE_COLORS] || '#3b82f6' }}
                        />
                        <span className="text-[9px] leading-tight text-[var(--text-secondary)] truncate hidden sm:inline">
                          {ev.title}
                        </span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <span className="text-[8px] text-[var(--text-tertiary)] pl-3">+{dayEvents.length - 2} more</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day popover */}
      {selectedDay && popoverPos && (
        <div
          ref={popoverRef}
          className="fixed z-40 w-64 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--border-color)]">
            <h4 className="text-xs font-semibold text-[var(--text-primary)]">
              {selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </h4>
            <button onClick={closePopover} className="p-1 rounded-md hover:bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-3 py-2 max-h-48 overflow-y-auto">
            {selectedDayEvents.length === 0 ? (
              <p className="text-[11px] text-[var(--text-tertiary)] py-1">No events on this day.</p>
            ) : (
              <div className="space-y-1.5">
                {selectedDayEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => openEditEvent(ev)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-left"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: EVENT_TYPE_COLORS[ev.event_type as keyof typeof EVENT_TYPE_COLORS] || '#3b82f6' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[var(--text-primary)] truncate">{ev.title}</p>
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        {new Date(ev.event_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isPastDate(selectedDay, today) && (
            <div className="px-3 py-2 border-t border-[var(--border-color)]">
              <button
                onClick={() => openNewEvent(selectedDay)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--accent-primary)]/10 hover:bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] text-xs font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Event
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upcoming events */}
      <div className="p-4 rounded-xl bg-[var(--surface-elevated)] border border-[var(--border-color)]">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-violet-400" />
          Upcoming
        </h3>
        {isLoading ? (
          <p className="text-xs text-[var(--text-tertiary)]">Loading...</p>
        ) : upcomingEvents.length === 0 ? (
          <p className="text-xs text-[var(--text-tertiary)]">No upcoming events this month. Add one above!</p>
        ) : (
          <div className="space-y-2">
            {upcomingEvents.map((ev) => (
              <button
                key={ev.id}
                onClick={() => openEditEvent(ev)}
                className="w-full flex items-center gap-3 py-2 hover:bg-[var(--bg-tertiary)] rounded-lg px-2 -mx-2 transition-colors text-left"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: EVENT_TYPE_COLORS[ev.event_type as keyof typeof EVENT_TYPE_COLORS] || '#3b82f6' }}
                />
                <span className="flex-1 text-sm text-[var(--text-primary)] truncate">{ev.title}</span>
                <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">
                  {new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <AddEventModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingEvent(null); setModalDefaultDate(''); }}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        subjects={subjects}
        editingEvent={editingEvent}
        defaultDate={modalDefaultDate}
      />
    </div>
  );
};
